import {
  buildCascadeTree,
  cascadeSchema,
  formatDate,
  OBJECTIVE_HORIZON_LABELS,
  OBJECTIVE_LEVEL_LABELS,
  OBJECTIVE_RELATION_LABELS,
  OBJECTIVE_STATUS_LABELS,
  objectiveOptionsSchema,
  objectiveSchema,
  targetPeriodLabel,
  type CascadeKpi,
  type CascadeTreeNode,
  type ObjectiveLevelName,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FolderKanban, Gauge, Link2, Network } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert } from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const LEVEL_STYLE: Record<ObjectiveLevelName, string> = {
  CORPORATE: 'bg-brand text-brand-foreground',
  DIRECTORATE: 'bg-brand-soft text-foreground',
  MANAGEMENT: 'border border-border-strong text-foreground',
};
const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
/** Valor com a unidade do KPI (R$ vem antes, como se escreve).*/
const withUnit = (v: number, unit: string) =>
  unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
/** Rótulo do semáforo: a performance em %, ou o estado quando não há número (sem dados, futuro). */
const performanceLabel = (performance: number | null, light: TrafficLight) =>
  performance !== null
    ? `${performance.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    : light === 'blue'
      ? 'Futuro'
      : 'Sem dados';

/** Estratégia › Desdobramento (T-045b, RF-20, PRD §11.20): corporativo → diretoria → gerência → metas. */
export function DesdobramentoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer>
      <PageHeader
        title="Desdobramento"
        description="Como os objetivos corporativos se desdobram em objetivos de diretoria e de gerência, até as metas operacionais de cada indicador."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O desdobramento pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <CascadeSection organizationId={organizationId} projectId={project.id} />
        </div>
      )}
    </PageContainer>
  );
}

function CascadeSection({ organizationId, projectId }: { organizationId: string; projectId: string }) {
  const { client } = useAuth();
  const base = `/organizations/${organizationId}/projects/${projectId}`;
  const [period, setPeriod] = useState<{ year: number; month: number } | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [perspectiveId, setPerspectiveId] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [opened, setOpened] = useState<CascadeTreeNode | null>(null);
  const ids = { year: useId(), month: useId(), department: useId(), perspective: useId() };

  const cascade = useQuery({
    queryKey: ['cascade', projectId, period],
    queryFn: ({ signal }) =>
      client.request(`${base}/cascade`, {
        query: period ? { year: String(period.year), month: String(period.month) } : undefined,
        schema: cascadeSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const options = useQuery({
    queryKey: ['objectives', projectId, 'options'],
    queryFn: ({ signal }) =>
      client.request(`${base}/objectives/options`, { schema: objectiveOptionsSchema, signal }),
  });
  const data = cascade.data;
  const year = period?.year ?? data?.year;
  const month = period?.month ?? data?.month;
  const filtered = Boolean(departmentId || perspectiveId);
  // A área escolhida vale com as áreas abaixo dela (diretoria → gerências).
  const areas = useMemo(() => {
    if (!departmentId) return null;
    const all = options.data?.departments ?? [];
    const set = new Set([departmentId]);
    for (let grew = true; grew;) {
      grew = false;
      for (const d of all) {
        if (d.parentId && set.has(d.parentId) && !set.has(d.id)) {
          set.add(d.id);
          grew = true;
        }
      }
    }
    return set;
  }, [departmentId, options.data]);
  const tree = useMemo(
    () =>
      data
        ? buildCascadeTree(
            data.nodes,
            (n) =>
              (!areas || (n.department !== null && areas.has(n.department.id))) &&
              (!perspectiveId || n.perspective.id === perspectiveId),
          )
        : [],
    [data, areas, perspectiveId],
  );
  const toggle = (id: string) =>
    setCollapsed((all) => {
      const next = new Set(all);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div>
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano das metas
          </label>
          <NativeSelect
            id={ids.year}
            value={year ?? ''}
            disabled={!data}
            onChange={(e) => setPeriod({ year: Number(e.target.value), month: month ?? 1 })}
          >
            {(data?.years ?? []).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
            Mês
          </label>
          <NativeSelect
            id={ids.month}
            value={month ?? ''}
            disabled={!data}
            onChange={(e) =>
              setPeriod({ year: year ?? new Date().getFullYear(), month: Number(e.target.value) })
            }
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.department} className="mb-1 block text-xs font-medium text-muted-foreground">
            Área
          </label>
          <NativeSelect
            id={ids.department}
            value={departmentId}
            disabled={!options.data}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Todas as áreas</option>
            {options.data?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.perspective} className="mb-1 block text-xs font-medium text-muted-foreground">
            Perspectiva
          </label>
          <NativeSelect
            id={ids.perspective}
            value={perspectiveId}
            disabled={!options.data}
            onChange={(e) => setPerspectiveId(e.target.value)}
          >
            <option value="">Todas as perspectivas</option>
            {options.data?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {options.isError ? (
        <FormAlert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível carregar as áreas e perspectivas dos filtros: {errorMessage(options.error)}
            <Button
              variant="outline"
              size="sm"
              loading={options.isFetching}
              onClick={() => void options.refetch()}
            >
              Tentar de novo
            </Button>
          </span>
        </FormAlert>
      ) : null}

      {cascade.isPending ? (
        <LoadingState rows={4} label="Carregando o desdobramento" />
      ) : cascade.isError ? (
        <ErrorState
          error={cascade.error}
          onRetry={() => void cascade.refetch()}
          retrying={cascade.isFetching}
        />
      ) : data!.nodes.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Network}
          title="Nenhum objetivo para desdobrar"
          description="Cadastre os objetivos com o nível (corporativo, diretoria ou gerência) e o objetivo que cada um desdobra; a árvore aparece aqui."
          action={
            <Button asChild variant="outline">
              <Link to={`/empresas/${organizationId}/estrategia/objetivos`}>Ir para os objetivos</Link>
            </Button>
          }
        />
      ) : tree.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Network}
          title="Nenhum objetivo com esses filtros"
          description="Troque a área ou a perspectiva para ver outros galhos do desdobramento."
          action={
            <Button
              variant="outline"
              onClick={() => {
                setDepartmentId('');
                setPerspectiveId('');
              }}
            >
              Limpar filtros
            </Button>
          }
        />
      ) : (
        <div aria-busy={cascade.isFetching}>
          <p className="mb-3 text-xs text-muted-foreground">
            Metas operacionais de {targetPeriodLabel('MONTH', data!.month).toLowerCase()}/{data!.year} (sem
            meta do mês, vale a anual).
            {data!.showPerformance
              ? data!.computedAt
                ? ` Performance e semáforo do mês e do acumulado, calculados em ${new Date(data!.computedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.`
                : ' Performance: ainda não há cálculo para este ano (cinza = sem dados).'
              : ''}
            {filtered
              ? ' Com filtro, os objetivos acima dos encontrados aparecem esmaecidos, para manter a cadeia.'
              : ''}
          </p>
          <CascadeList
            nodes={tree}
            label="Desdobramento dos objetivos"
            depth={0}
            collapsed={collapsed}
            onToggle={toggle}
            onOpen={setOpened}
            showKpis={data!.showKpis}
            showTargets={data!.showTargets}
            showPerformance={data!.showPerformance}
            year={data!.year}
            month={data!.month}
          />
        </div>
      )}

      <Dialog
        open={opened !== null}
        onOpenChange={(o) => !o && setOpened(null)}
        title="Objetivo"
        className="w-[min(40rem,calc(100vw-2rem))]"
      >
        {opened ? (
          <ObjectiveDetail organizationId={organizationId} base={base} node={opened} projectId={projectId} />
        ) : null}
      </Dialog>
    </div>
  );
}

function CascadeList({
  nodes,
  label,
  depth,
  ...rest
}: {
  nodes: CascadeTreeNode[];
  label: string;
  depth: number;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (node: CascadeTreeNode) => void;
  showKpis: boolean;
  showTargets: boolean;
  showPerformance: boolean;
  year: number;
  month: number;
}) {
  return (
    <ul
      aria-label={label}
      className={cn(
        'flex flex-col gap-2',
        depth > 0 && 'ml-2 mt-2 border-l-2 border-border pl-3 sm:ml-4 sm:pl-5',
      )}
    >
      {nodes.map((n) => (
        <CascadeItem key={n.id} node={n} depth={depth} {...rest} />
      ))}
    </ul>
  );
}

function CascadeItem({
  node,
  depth,
  collapsed,
  onToggle,
  onOpen,
  showKpis,
  showTargets,
  showPerformance,
  year,
  month,
}: {
  node: CascadeTreeNode;
  depth: number;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (node: CascadeTreeNode) => void;
  showKpis: boolean;
  showTargets: boolean;
  showPerformance: boolean;
  year: number;
  month: number;
}) {
  const closed = collapsed.has(node.id);
  const hasChildren = node.children.length > 0 || (showKpis && node.kpis.length > 0);
  return (
    <li>
      <div
        className={cn(
          'rounded-lg border border-border bg-surface p-3',
          node.context && 'border-dashed bg-transparent opacity-70',
        )}
      >
        <div className="flex items-start gap-2">
          {hasChildren ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1 shrink-0"
              aria-expanded={!closed}
              aria-label={`${closed ? 'Expandir' : 'Recolher'} ${node.name}`}
              onClick={() => onToggle(node.id)}
            >
              {closed ? <ChevronRight aria-hidden /> : <ChevronDown aria-hidden />}
            </Button>
          ) : (
            <span className="size-8 shrink-0" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap',
                  LEVEL_STYLE[node.level],
                )}
              >
                {OBJECTIVE_LEVEL_LABELS[node.level]}
              </span>
              <button
                type="button"
                className="text-left font-semibold underline-offset-2 hover:underline focus-visible:underline"
                onClick={() => onOpen(node)}
              >
                {node.name}
              </button>
              {showPerformance ? (
                <span className="flex items-center gap-1.5">
                  <StatusBadge
                    status={node.performance?.light ?? 'gray'}
                    label={`Mês ${performanceLabel(node.performance?.performance ?? null, node.performance?.light ?? 'gray')}`}
                  />
                  {node.performance ? (
                    <StatusBadge
                      status={node.performance.ytdLight}
                      label={`Acumulado ${performanceLabel(node.performance.ytdPerformance, node.performance.ytdLight)}`}
                    />
                  ) : null}
                  {node.performance?.source === 'CHILDREN' ? (
                    <span className="text-xs text-muted-foreground">consolidado dos desdobramentos</span>
                  ) : null}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {node.department?.name ?? 'Empresa toda'} · {node.perspective.name} ·{' '}
              {OBJECTIVE_STATUS_LABELS[node.status]}
              {node.context ? ' · fora do filtro' : ''}
            </p>
            {node.parentHidden ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Desdobra um objetivo de área fora do seu acesso.
              </p>
            ) : null}
            {node.horizontal.length ? (
              <p className="mt-1 flex items-start gap-1 text-xs">
                <Link2 className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span>
                  <span className="text-muted-foreground">Relação horizontal com </span>
                  {node.horizontal.map((h) => h.name).join(', ')}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        {!closed && showKpis && node.kpis.length ? (
          <ul
            aria-label={`Metas operacionais de ${node.name}`}
            className="mt-2 flex flex-col gap-1.5 sm:ml-10"
          >
            {node.kpis.map((k) => (
              <KpiLine
                key={k.id}
                kpi={k}
                showTarget={showTargets}
                showPerformance={showPerformance}
                year={year}
                month={month}
              />
            ))}
          </ul>
        ) : null}
      </div>
      {!closed && node.children.length ? (
        <CascadeList
          nodes={node.children}
          label={`Desdobramentos de ${node.name}`}
          depth={depth + 1}
          collapsed={collapsed}
          onToggle={onToggle}
          onOpen={onOpen}
          showKpis={showKpis}
          showTargets={showTargets}
          showPerformance={showPerformance}
          year={year}
          month={month}
        />
      ) : null}
    </li>
  );
}

function KpiLine({
  kpi,
  showTarget,
  showPerformance,
  year,
  month,
}: {
  kpi: CascadeKpi;
  showTarget: boolean;
  showPerformance: boolean;
  year: number;
  month: number;
}) {
  const target = kpi.target;
  const result = kpi.month;
  return (
    <li className="flex flex-col gap-0.5 rounded-md bg-muted/60 px-2.5 py-1.5 text-xs sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="flex min-w-0 items-center gap-1.5">
        <Gauge className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="font-mono text-muted-foreground">{kpi.code}</span>
        <span className="truncate font-medium">{kpi.name}</span>
      </span>
      {showTarget || showPerformance ? (
        <span className="flex flex-col gap-1 tabular-nums sm:items-end sm:text-right">
          {showTarget ? (
            <span>
              {target
                ? `${target.periodType === 'MONTH' ? `Meta de ${targetPeriodLabel('MONTH', month).toLowerCase()}/${year}` : `Meta anual ${year}`}: ${withUnit(target.value, kpi.unitLabel)}`
                : `Sem meta definida para ${year}`}
            </span>
          ) : null}
          {showPerformance ? (
            <span className="flex flex-wrap items-center gap-2 sm:justify-end">
              <span>
                {result?.actual == null && result?.target == null
                  ? 'Sem realizado no mês'
                  : `Realizado ${result.actual != null ? withUnit(result.actual, kpi.unitLabel) : '—'} x meta do mês ${result.target != null ? withUnit(result.target, kpi.unitLabel) : '—'}`}
              </span>
              <StatusBadge
                status={result?.light ?? 'gray'}
                label={performanceLabel(result?.performance ?? null, result?.light ?? 'gray')}
              />
            </span>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

function ObjectiveDetail({
  organizationId,
  projectId,
  base,
  node,
}: {
  organizationId: string;
  projectId: string;
  base: string;
  node: CascadeTreeNode;
}) {
  const { client } = useAuth();
  const detail = useQuery({
    queryKey: ['objectives', projectId, 'detail', node.id],
    queryFn: ({ signal }) =>
      client.request(`${base}/objectives/${node.id}`, { schema: objectiveSchema, signal }),
  });
  const o = detail.data;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs text-muted-foreground">
          {OBJECTIVE_LEVEL_LABELS[node.level]} · {node.department?.name ?? 'Empresa toda'} ·{' '}
          {node.perspective.name}
        </p>
        <p className="mt-1 text-lg font-semibold">{node.name}</p>
      </div>
      {detail.isPending ? (
        <LoadingState rows={3} label="Carregando o objetivo" />
      ) : detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} retrying={detail.isFetching} />
      ) : (
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          {o!.description ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Descrição</dt>
              <dd>{o!.description}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-xs text-muted-foreground">Situação</dt>
            <dd>{OBJECTIVE_STATUS_LABELS[o!.status]}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Responsável</dt>
            <dd>{o!.owner?.name ?? 'Sem responsável'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Período</dt>
            <dd>
              {o!.startDate || o!.endDate
                ? `${o!.startDate ? formatDate(o!.startDate) : '…'} a ${o!.endDate ? formatDate(o!.endDate) : '…'}`
                : OBJECTIVE_HORIZON_LABELS[o!.horizon]}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Desdobra</dt>
            <dd>
              {o!.parent ? o!.parent.name : o!.parentHidden ? 'Objetivo de área fora do seu acesso' : '—'}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Desdobrado em</dt>
            <dd>
              {o!.children.length
                ? o!.children
                    .map(
                      (c) =>
                        `${c.name} (${OBJECTIVE_LEVEL_LABELS[c.level].toLowerCase()}${c.archived ? ', arquivado' : ''})`,
                    )
                    .join(', ')
                : 'Nenhum objetivo'}
            </dd>
          </div>
          {o!.relations.length + o!.relatedFrom.length ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Relações</dt>
              <dd>
                <ul className="flex flex-col gap-0.5">
                  {[...o!.relations, ...o!.relatedFrom].map((r) => (
                    <li key={`${r.type}:${r.objective.id}`}>
                      {OBJECTIVE_RELATION_LABELS[r.type]}: {r.objective.name}
                      {r.objective.archived ? ' (arquivado)' : ''}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
        </dl>
      )}
      <div className="flex justify-end">
        <Button asChild variant="outline">
          <Link to={`/empresas/${organizationId}/estrategia/objetivos?q=${encodeURIComponent(node.name)}`}>
            Abrir em Objetivos
          </Link>
        </Button>
      </div>
    </div>
  );
}
